import React, { useState, useEffect } from 'react';
import { X, RefreshCw, GitBranch, Phone, User, Clock, Hash } from 'lucide-react';
import { api } from '../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';
import { useNavigate } from 'react-router-dom';

interface FlowConversation {
  id: string;
  protocolId: string;
  customerName: string | null;
  customerPhone: string;
  status: string;
  startedAt: string;
  sessionData: {
    menuState: string | null;
    selectedOption: string | null;
    lastActivity: string;
  } | null;
}

interface FlowMonitorModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const FlowMonitorModal: React.FC<FlowMonitorModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<FlowConversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const loadFlowConversations = async () => {
    setIsLoading(true);
    try {
      const data = await api.getFlowConversations();
      setConversations(data);
    } catch (error) {
      console.error('Failed to load flow conversations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadFlowConversations();
    }
  }, [isOpen]);

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    const saoPauloTime = toZonedTime(date, 'America/Sao_Paulo');
    return format(saoPauloTime, 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      WAITING: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400', label: 'Aguardando' },
      MENU: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', label: 'No Menu' },
      QUEUED: { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', label: 'Na Fila' },
      ACTIVE: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400', label: 'Ativo' },
    };
    const badge = badges[status] || { bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', label: status };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getMenuStateLabel = (menuState: string | null) => {
    if (!menuState) return 'Sem estado';

    const states: Record<string, string> = {
      'MAIN_MENU': 'Menu Principal',
      'WAITING_DEPARTMENT': 'Aguardando Departamento',
      'WAITING_MESSAGE': 'Aguardando Mensagem',
      'SATISFACTION_SURVEY': 'Pesquisa de Satisfação',
      'RATING': 'Avaliação',
    };

    return states[menuState] || menuState;
  };

  const handleConversationClick = (conversationId: string) => {
    onClose();
    navigate(`/conversations/${conversationId}`);
  };

  const filteredConversations = conversations.filter(conv => {
    if (filterStatus === 'all') return true;
    return conv.status === filterStatus;
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <GitBranch className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Monitoramento de Fluxo Automatizado
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {filteredConversations.length} conversa(s) em fluxo
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={loadFlowConversations}
              disabled={isLoading}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg transition disabled:opacity-50 flex items-center space-x-1.5 text-sm font-medium"
              title="Atualizar lista"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span>Atualizar</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Filtrar:</span>
            {['all', 'WAITING', 'MENU', 'QUEUED'].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1 rounded-lg text-sm transition ${
                  filterStatus === status
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {status === 'all' ? 'Todos' : status}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && conversations.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="text-center py-12">
              <GitBranch className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">
                Nenhuma conversa em fluxo automatizado no momento
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {filteredConversations.map((conv) => (
                <div
                  key={conv.id}
                  onClick={() => handleConversationClick(conv.id)}
                  className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition cursor-pointer group"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <User className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {conv.customerName || 'Cliente'}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                        <Phone className="w-3.5 h-3.5" />
                        <span>{conv.customerPhone}</span>
                      </div>
                    </div>
                    {getStatusBadge(conv.status)}
                  </div>

                  {/* Protocol */}
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 mb-2">
                    <Hash className="w-3.5 h-3.5" />
                    <span className="font-mono">{conv.protocolId}</span>
                  </div>

                  {/* Flow State */}
                  <div className="bg-white dark:bg-gray-800 rounded-md p-3 mb-3">
                    <div className="flex items-center space-x-2 mb-1">
                      <GitBranch className="w-4 h-4 text-purple-500 dark:text-purple-400" />
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        Estado do Fluxo:
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">
                      {getMenuStateLabel(conv.sessionData?.menuState || null)}
                    </span>
                    {conv.sessionData?.selectedOption && (
                      <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                        Opção: <strong>{conv.sessionData.selectedOption}</strong>
                      </div>
                    )}
                  </div>

                  {/* Timing */}
                  <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <div className="flex items-center space-x-1">
                      <Clock className="w-3.5 h-3.5" />
                      <span>Última atividade:</span>
                    </div>
                    <span className="font-medium">
                      {conv.sessionData?.lastActivity
                        ? formatDateTime(conv.sessionData.lastActivity)
                        : formatDateTime(conv.startedAt)
                      }
                    </span>
                  </div>

                  {/* Hover effect */}
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600 text-center opacity-0 group-hover:opacity-100 transition">
                    <span className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                      Clique para abrir conversa →
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
